import React, { ReactNode } from "react";
import { useParams } from "react-router";
import { Box, Flex, Grid } from "@chakra-ui/react";
import CenteredSpinner from "@/shared/components/centered-spinner";
import CustomNoResults from "@/shared/components/no-results/CustomNoResults";
import PageHeader from "@/shared/components/PageHeader";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import PathPickOrderDetailRemittanceBanner from "@/features/pathpick/features/order-history/features/detail/components/overview-section/PathPickOrderDetailRemittanceBanner";
import PathPickOrderDetailCancelBanner from "@/features/pathpick/features/order-history/features/detail/components/overview-section/PathPickOrderDetailCancelBanner";
import PathPickOrderDetailHeader from "@/features/pathpick/features/order-history/features/detail/components/overview-section/PathPickOrderDetailHeader";
import PathPickOrderDetailInvoice from "@/features/pathpick/features/order-history/features/detail/components/document-section/PathPickOrderDetailInvoice";
import PathPickOrderDetailKreamFile from "@/features/pathpick/features/order-history/features/detail/components/document-section/PathPickOrderDetailKreamFile";
import PathPickOrderDetailProducts from "@/features/pathpick/features/order-history/features/detail/components/products-section/PathPickOrderDetailProducts";
import PathPickOrderDetailRemittance from "@/features/pathpick/features/order-history/features/detail/components/payment-section/PathPickOrderDetailRemittance";
import PathPickOrderDetailShipping from "@/features/pathpick/features/order-history/features/detail/components/shipping-section/PathPickOrderDetailShipping";
import PathPickOrderDetailStepper from "@/features/pathpick/features/order-history/features/detail/components/overview-section/PathPickOrderDetailStepper";
import PathPickOrderDetailSummary from "@/features/pathpick/features/order-history/features/detail/components/overview-section/PathPickOrderDetailSummary";
import useGetPathPickOrderDetail from "@/features/pathpick/features/order-history/features/detail/hooks/query/useGetPathPickOrderDetail";
import { ORDER_DETAIL_KEY } from "@/features/pathpick/features/order-history/features/detail/constants";
import { PathPickOrderDetailResponse } from "@/features/pathpick/features/order-history/types/response";
import {
  PathPickOrderDetailPreview,
  PathPickOrderDetailSectionKey,
} from "@/features/pathpick/features/order-history/features/detail/types";
import {
  getPathPickOrderDetailSections,
  toPathPickOrderSteps,
} from "@/features/pathpick/features/order-history/features/detail/utils";
import { Helmet } from "react-helmet-async";

// 제목과 뒤로가기는 조회 결과에 의존하지 않으므로 로딩/빈 분기보다 위에 둔다.
// PageHeader는 자체 패딩과 하단 헤어라인을 가진 full-bleed 바라 스크롤 영역 바깥에 놓아야 한다.
function PathPickOrderHistoryDetailPage() {
  const { t, tTitle } = useI18nHelper();
  const pageTitle = `${tTitle("common:navigation.path_pick")} | ${tTitle(
    `${ORDER_DETAIL_KEY}title`
  )}`;
  const { navigate } = useRouterHelper();

  return (
    <Flex flexDirection={"column"} height={"100%"}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} />
      </Helmet>
      <PageHeader
        iconName={"chevron-left"}
        iconSize={28}
        title={t(`${ORDER_DETAIL_KEY}title`)}
        color={"#1D1D1B"}
        onIconClick={() => navigate("/pathpick/order-history")}
      />
      <Flex
        sx={{
          padding: "16px",
          paddingBottom: "100px",
          overflow: "auto",
          flex: 1,
          alignItems: "flex-start",
          justifyContent: "center",
          background: "#F7F9FA",
        }}
      >
        <PathPickOrderHistoryDetailContent />
      </Flex>
    </Flex>
  );
}

// 로딩·빈 상태·본문이 같은 폭을 쓰도록 세 분기를 감싸는 컬럼.
// 좁은 폭에서는 minW 하한을 풀어야 페이지 전체가 가로로 밀리지 않는다. md에서
// 800px를 유지하는 것은 768~800px 구간(데스크탑 판정 구간)에서 2열 배치가 눌리지
// 않게 하기 위해서다.
function PathPickOrderDetailColumn({ children }: { children: ReactNode }) {
  return (
    <Flex
      flexDirection={"column"}
      gap={"16px"}
      width={"100%"}
      maxWidth={"1120px"}
      minW={{ base: "0", md: "800px" }}
    >
      {children}
    </Flex>
  );
}

function PathPickOrderHistoryDetailContent() {
  const { t } = useI18nHelper();
  const { id } = useParams();

  // order_id 자리에는 숫자 id와 주문번호(PP-...) 모두 들어갈 수 있어 문자열을 그대로 넘긴다.
  const { pathPickOrderDetail, isPending } = useGetPathPickOrderDetail(id);

  if (isPending) {
    return (
      <PathPickOrderDetailColumn>
        <CenteredSpinner padding={"48px 0"} />
      </PathPickOrderDetailColumn>
    );
  }

  if (!pathPickOrderDetail) {
    return (
      <PathPickOrderDetailColumn>
        <CustomNoResults title={t("path_pick:order_history.no_results")} />
      </PathPickOrderDetailColumn>
    );
  }

  return <PathPickOrderDetailBody order={pathPickOrderDetail} />;
}

interface PathPickOrderDetailBodyProps {
  order: PathPickOrderDetailResponse;
  // 시작 가이드의 화면 미리보기 — 있으면 실제 조회/저장을 걸지 않고 KREAM 파일 카드만 살려 둔다.
  preview?: PathPickOrderDetailPreview;
}

/**
 * 주문 상세 본문 — 헤더/요약 스트립/섹션 전체.
 *
 * 페이지 셸(제목 바·스크롤 영역)과 조회를 분리해 둔 덕에, 시작 가이드가 목 주문을 물려
 * 같은 화면을 그대로 그릴 수 있다.
 */
export function PathPickOrderDetailBody({
  order,
  preview,
}: PathPickOrderDetailBodyProps) {
  return (
    <PathPickOrderDetailColumn>
      {/* 헤더와 요약 스트립은 모든 상태에서 위치가 같다 */}
      <PathPickOrderDetailHeader order={order} />
      <PathPickOrderDetailSummary order={order} />
      <PathPickOrderDetailSections order={order} preview={preview} />
    </PathPickOrderDetailColumn>
  );
}

// 상태별 배치 차이는 getPathPickOrderDetailSections가 돌려주는 키 순서로만 표현된다.
// 섹션 JSX는 아래 맵에 한 번씩만 등장하므로 배치가 바뀌어도 JSX를 옮길 필요가 없다.
function PathPickOrderDetailSections({
  order,
  preview,
}: {
  order: PathPickOrderDetailResponse;
  preview?: PathPickOrderDetailPreview;
}) {
  const isPreview = Boolean(preview);
  const remittance = (
    <PathPickOrderDetailRemittance order={order} preview={preview} />
  );
  const shipping = <PathPickOrderDetailShipping order={order} />;
  const invoice = <PathPickOrderDetailInvoice invoice={order.invoice} />;
  // 취소된 주문은 "수량 확정 후 가능"도 "파일 준비 가능"도 성립하지 않아 카드를 걷어낸다.
  const kreamFile =
    order.status === "CANCELED" ? null : (
      <PathPickOrderDetailKreamFile
        order={order}
        // 미리보기에서는 `등록 신청 내역 보기` 링크를 감춘다 — 누르면 가이드를 벗어난다.
        showHistoryAction={!isPreview}
        // 미리보기는 데모 주문이라 신청 API를 호출하지 않고 콜백만 부른다.
        isPreview={isPreview}
        // 가이드가 이 카드를 누르게 하려는 경우에만 강조한다 — 신청 콜백이 그 신호다.
        isHighlighted={Boolean(preview?.onKreamFileRequested)}
        onRequested={preview?.onKreamFileRequested}
      />
    );

  const sectionNodes: Record<PathPickOrderDetailSectionKey, ReactNode> = {
    banner: <PathPickOrderDetailRemittanceBanner order={order} />,
    cancelBanner: (
      <PathPickOrderDetailCancelBanner
        cancelReason={order.cancelReason}
        canceledAt={order.canceledAt}
      />
    ),
    progress: (
      <PathPickOrderDetailStepper steps={toPathPickOrderSteps(order)} />
    ),
    products: (
      // 옵션 표는 카드 안에서 좌우 스크롤로 읽는 부분이라, 미리보기의 클릭 차단을 걷어낸다.
      // 표 안에 눌리는 요소는 없어서 되살려도 동작하는 것은 스크롤뿐이다.
      <PreviewInteractive isEnabled={isPreview}>
        <PathPickOrderDetailProducts order={order} />
      </PreviewInteractive>
    ),
    remittanceShippingGrid: (
      // 좁은 폭에서는 1열로 쌓되 DOM 순서(입금 → 배송/송장/KREAM)를 그대로 둔다 —
      // order로 뒤집으면 화면 순서와 보조기기가 읽는 순서가 어긋난다.
      <Grid
        templateColumns={{ base: "1fr", md: "1.5fr 1fr" }}
        gap={"16px"}
        alignItems={"stretch"}
      >
        {remittance}
        <Flex flexDirection={"column"} gap={"16px"}>
          {shipping}
          {invoice}
          {kreamFile}
        </Flex>
      </Grid>
    ),
  };

  return (
    <>
      {getPathPickOrderDetailSections(order).map((key) => (
        <React.Fragment key={key}>{sectionNodes[key]}</React.Fragment>
      ))}
    </>
  );
}

// 미리보기는 화면 전체의 클릭을 막아 두므로, 살아 있어야 하는 영역만 다시 켠다.
// 실제 화면에서는 래퍼 요소를 만들지 않는다.
function PreviewInteractive({
  isEnabled,
  children,
}: {
  isEnabled: boolean;
  children: ReactNode;
}) {
  if (!isEnabled) {
    return <>{children}</>;
  }

  return <Box pointerEvents={"auto"}>{children}</Box>;
}

export default PathPickOrderHistoryDetailPage;
